import { PriceOptionData } from 'src/admin/listing-editor/components/PricingPanel/pricingPanelHelper';
import { MAXIMUM_HOURS_LIMIT, MAXIMUM_MULTIDAY_LIMIT, MINIMUM_LIMIT } from 'src/admin/listing-editor/constants';

export const validatePriceTierExists = (
  { rate = 0, duration = 0 }: PriceOptionData,
): boolean => Boolean(rate + duration);

export const removeEmptyPriceTiers = (tier: PriceOptionData[] = []): PriceOptionData[] => (
  tier.filter(validatePriceTierExists)
);

export const validateAnyPriceTiersExist = (tiers: PriceOptionData[] = []): boolean => (
  tiers.some(validatePriceTierExists)
);

/**
 * Ensure that each tier has a duration greater than the previous tier
 * Check current is greater or equal to limit
 */
export const validatePriceTierDurationMin = ({
  current,
  previous,
  limit = -Infinity,
}: {
  current: PriceOptionData;
  previous: PriceOptionData;
  limit?: number;
}): boolean => {
  const { duration } = current;
  if (!previous) return duration >= limit;

  return duration > previous.duration && duration >= limit;
};

export const validatePriceTierDurationMax = ({
  current,
  next,
  limit = Infinity,
}: {
  current: PriceOptionData;
  next: PriceOptionData;
  limit?: number;
}): boolean => {
  const { duration } = current;
  if (!next) return duration <= limit;

  return duration < next.duration && duration <= limit;
};

/** Value is required if dependency has a value */
export const validateValueWithDependency = (value: number, dependency: number): boolean => !(
  dependency > 0 && value === 0
);

/**
 * Duration is required if rate has a value
 * Ignore when all are zero
 */
export const validatePriceTierDurationWithRate = (
  { rate, duration }: PriceOptionData,
): boolean => validateValueWithDependency(duration, rate);

/**
 * Rate is required if duration has a value
 * Ignore when all are zero
 */
export const validatePriceTierRateWithDuration = (
  { rate, duration }: PriceOptionData,
): boolean => validateValueWithDependency(rate, duration);

export const validateMaxValueRequired = (tiers: PriceOptionData[], value: number): boolean => !(
  tiers.length && !value
);

// Refund is required if anticipation has a value
export const validateCancellationRefundWithAnticipation = (
  refund: number,
  anticipation: number,
): boolean => (
  !(anticipation > 0 && refund === 0)
);

// Anticipation is required if refund has a value
export const validateCancellationAnticipationWithRefund = (
  anticipation: number,
  refund: number,
): boolean => (
  !(refund > 0 && anticipation === 0)
);

export type VerboseValidatePriceTierResult = {
  /** All validations have passed */
  valid?: boolean;
  /** Duration constraints */
  duration?: {
    /** Tier is the same or less than the next tier, or at minimum limit applied to all tiers */
    min?: number;
    /** Tier is the same or more than the next tier */
    max?: number;
    /** Tier has hit the duration limit applied to all tiers */
    allMax?: number;
  };
  /** Required fields are missing */
  required?: {
    /** Rate is required if duration has a value */
    duration?: boolean;
    /** Duration is required if rate has a value */
    rate?: boolean;
  };
};

type VerboseValidatePriceTier = {
  /** Tier being validated */
  currentTier: PriceOptionData;
  /** Previous tier, if it exists, currentTier duration and capacity must be higher */
  previousTier: PriceOptionData;
  /** Next tier, if it exists, currentTier duration and capacity must be lower */
  nextTier: PriceOptionData;
  /** Duration limits will be applied to all tiers. These values will be used instead if
    * next or previous tier values are higher (max), or lower (min) */
  durationLimit?: {
    min?: number;
    max?: number;
  };
};
export const verboseValidatePriceTier = ({
  currentTier: current,
  previousTier: previous,
  nextTier: next,
  durationLimit: {
    min: durationMin,
    max: durationMax,
  },
}: VerboseValidatePriceTier): VerboseValidatePriceTierResult => {
  const durationError: VerboseValidatePriceTierResult['duration'] = {};
  const requiredError: VerboseValidatePriceTierResult['required'] = {};

  if (!validatePriceTierRateWithDuration(current)) {
    requiredError.rate = true;
  }
  if (!validatePriceTierDurationWithRate(current)) {
    requiredError.duration = true;
  }
  if (!validatePriceTierDurationMin({ current, previous, limit: durationMin })) {
    durationError.min = previous?.duration;
  }
  if (!validatePriceTierDurationMax({ current, next, limit: durationMax })) {
    durationError.max = next?.duration;
    if (next?.duration > durationMax || !next) {
      durationError.max = null;
      durationError.allMax = durationMax;
    }
  }

  const result: VerboseValidatePriceTierResult = {
    ...(Object.keys(durationError).length && { duration: durationError }),
    ...(Object.keys(requiredError).length && { required: requiredError }),
  };

  return {
    ...result,
    valid: !Object.keys(result).length,
  };
};

export type VerboseValidateAllPriceTiersResult = {
  /** All validations have passes */
  valid?: boolean;
  /** Results for each hour tier */
  hour: VerboseValidatePriceTierResult[];
  /** Results for each multiday tier */
  multiday: VerboseValidatePriceTierResult[];
  required?: {
    /** At least one tier is required */
    tiers?: boolean;
    /** Missing the max value for hour tiers */
    hourMax?: boolean;
    /** Missing the max value for multiday tiers */
    multidayMax?: boolean;
    /** Refund is required if anticipation has a value */
    cancellationRefund?: boolean;
    /** Anticipation is required if refund has a value */
    cancellationAnticipation?: boolean;
  };
  /** Duration limits */
  limit?: {
    /** Max hours value over the global limit (MAXIMUM_HOURS_LIMIT) */
    hourMax?: number;
    /** Max multiday value is over the global limit (MAXIMUM_MULTIDAY_LIMIT) */
    multidayMax?: number;
  };
};

type VerboseValidateAllPriceTiers = {
  /** Hourly price tiers */
  hour: PriceOptionData[];
  /** Multiday price tiers */
  multiday: PriceOptionData[];
  /** Duration limits will be applied to all tiers */
  durationLimit: {
    min: number;
    /** Upper bound for hour-type tiers */
    hourMax: number;
    /** Upper bound for day and overnight-type tiers */
    multidayMax: number;
  };
  /** Cancellation terms */
  cancellation: {
    /** Refund percentage */
    refund: number;
    /** Number of days required to cancel */
    anticipation: number;
  };
};

type AllTierValidationResult = {
  hour: VerboseValidatePriceTierResult[];
  multiday: VerboseValidatePriceTierResult[];
};

/**
 * Fully validate all price tiers and any extra fields e.g. cancellation
 * The error response will detail exactly which parts have failed validation.
 * These params have extra constraints:
 *
 * @param {Object} durationLimit `min` will be clamped to MINIMUM_LIMIT
 *                               `hourMax` will be clamped to MAXIMUM_HOURS_LIMIT
 *                               `multidayMax` will be clamped to MAXIMUM_MULTIDAY_LIMIT
 */
export const verboseValidateAllPriceTiers = ({
  hour,
  multiday,
  durationLimit,
  cancellation,
}: VerboseValidateAllPriceTiers): VerboseValidateAllPriceTiersResult => {
  const hourTiers = removeEmptyPriceTiers(hour);
  const multidayTiers = removeEmptyPriceTiers(multiday);

  const allTiers = [...hourTiers, ...multidayTiers];

  const requiredError: VerboseValidateAllPriceTiersResult['required'] = {};

  if (!validateAnyPriceTiersExist(allTiers)) {
    requiredError.tiers = true;
  }

  // Clamp duration limits
  const durationMin = Math.max(MINIMUM_LIMIT, durationLimit.min);
  const durationHoursMax = Math.min(MAXIMUM_HOURS_LIMIT, durationLimit.hourMax);
  const durationMultidayMax = Math.min(MAXIMUM_MULTIDAY_LIMIT, durationLimit.multidayMax);

  if (!validateMaxValueRequired(hourTiers, durationLimit.hourMax)) {
    requiredError.hourMax = true;
  }

  if (!validateMaxValueRequired(multidayTiers, durationLimit.multidayMax)) {
    requiredError.multidayMax = true;
  }

  const limitError: VerboseValidateAllPriceTiersResult['limit'] = {};
  if (durationHoursMax < durationLimit.hourMax) {
    limitError.hourMax = durationHoursMax;
  }

  if (durationMultidayMax < durationLimit.multidayMax) {
    limitError.multidayMax = durationMultidayMax;
  }

  if (!validateCancellationRefundWithAnticipation(cancellation.refund, cancellation.anticipation)) {
    requiredError.cancellationRefund = true;
  }

  if (!validateCancellationAnticipationWithRefund(cancellation.anticipation, cancellation.refund)) {
    requiredError.cancellationAnticipation = true;
  }

  // Validate every tier separately
  const allTierResults: AllTierValidationResult = Object.entries({
    hour: hourTiers,
    multiday: multidayTiers,
  }).reduce((results, [tierType, tiers]) => {
    let durationMax;
    if (tierType === 'hour') {
      durationMax = durationHoursMax;
    } else {
      durationMax = durationMultidayMax;
    }
    const tierResult = tiers.map((currentTier, i) => (
      verboseValidatePriceTier({
        currentTier,
        previousTier: i > 0 ? tiers[i - 1] : null,
        nextTier: i < tiers.length - 1 ? tiers[i + 1] : null,
        durationLimit: {
          min: durationMin,
          max: durationMax,
        },
      })
    ));
    return { ...results, [tierType]: tierResult };
  }, {} as AllTierValidationResult);

  const isMissingRequired = Object.keys(requiredError).length;
  const isOverLimit = Object.keys(limitError).length;
  const isValid = (
    allTierResults.hour.every(({ valid }) => valid)
    && allTierResults.multiday.every(({ valid }) => valid)
    && !isMissingRequired
    && !isOverLimit
  );

  return {
    valid: isValid,
    ...(isMissingRequired && { required: requiredError }),
    ...(isOverLimit && { limit: limitError }),
    ...allTierResults,
  };
};
